import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Custom pull-to-refresh for the INSTALLED (standalone) PWA.
 *
 * Why this exists:
 *  - The app contains overscroll (overscroll-behavior: none on html/body,
 *    see globals.css) to kill the iOS rubber-band that washes the safe-area
 *    past the sticky header. That containment is left UNTOUCHED, so neither
 *    platform's native pull-to-refresh fires. This component supplies the
 *    pull-to-reload gesture itself, cross-platform, without relaxing
 *    overscroll — so the rubber-band fix stays intact on iOS.
 *
 * Safety / non-interference:
 *  - Only arms in standalone display-mode (browser tabs keep native
 *    behaviour; the desktop browser is unaffected). Both iOS and Android
 *    installed PWAs get it.
 *  - Only starts a pull when the scroll container is exactly at the top
 *    (scrollTop <= 0) AND the gesture is clearly vertical-downward — a
 *    horizontal swipe (sidebar gesture, carousels) or an upward scroll is
 *    ignored, and once the user scrolls down the gesture is abandoned.
 *  - Never calls preventDefault on touchmove (listeners are passive), so it
 *    cannot block normal scrolling or the zoning-canvas / drag handlers.
 *
 * @param scrollRef ref to the actual scroll container (Layout's <main>).
 */
const PULL_THRESHOLD = 70; // px of pull past the top before a release reloads
const MAX_INDICATOR = 90; // px — clamp the indicator travel

// Arm on any TOUCH device — installed PWA (standalone) AND mobile browser
// tabs. In a browser the native pull-to-refresh is already killed by
// overscroll-behavior:none, so this is the only pull-to-reload there too;
// desktop (fine pointer) is excluded so a mouse drag never triggers a reload.
function isPullToReloadEnabled(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    // iOS Safari exposes this non-standard flag for home-screen webapps.
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches === true;
  return standalone || coarsePointer;
}

/**
 * The element that actually scrolls under this finger.
 *
 * `<main>` is not always the scroller: the analytics tabs (речь, очередь,
 * столы, визиты, тепловая карта) are `overflow-y-auto` panels that scroll
 * INSIDE themselves, so `<main>.scrollTop` sits at 0 forever. With only the
 * main container checked, every downward swipe through such a list read as
 * «pulling from the top» and dragged the refresh spinner out mid-scroll —
 * reported from mobile on the speech tab, 10.09.2026.
 *
 * Walk up from the touch target to the root and take the first element that
 * both declares vertical scrolling and has something to scroll. Falls back to
 * the root, so pages without an inner panel behave exactly as before.
 */
function nearestScroller(target: Element | null, root: HTMLElement): HTMLElement {
  let node: Element | null = target;
  while (node && node !== root) {
    if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight + 1) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
    }
    node = node.parentElement;
  }
  return root;
}

export function PullToRefresh({ scrollRef }: { scrollRef: React.RefObject<HTMLElement | null> }) {
  const [pull, setPull] = useState(0); // current visible pull distance (px)
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const startX = useRef<number | null>(null);
  const activeRef = useRef(false);
  // The scroller this gesture belongs to — `<main>` or an inner tab panel.
  const scrollerRef = useRef<HTMLElement | null>(null);
  // Latest pull, read by the touchend closure without re-binding listeners.
  const pullRef = useRef(0);
  pullRef.current = pull;

  useEffect(() => {
    if (!isPullToReloadEnabled()) return;
    const el = scrollRef.current;
    if (!el) return;

    function onTouchStart(e: TouchEvent) {
      // Stand down when the gesture starts inside an area that claims the pull
      // itself (e.g. the dashboard on a non-first page, where a downward swipe
      // pages back instead of refreshing — see ProofGrid data-owns-pull / R1).
      const target = e.target as Element | null;
      if (target?.closest?.('[data-owns-pull]')) {
        startY.current = null;
        return;
      }
      // Only consider starting a pull when the scroller UNDER THE FINGER is at
      // its very top — not merely when <main> is, which it always is on tabs
      // that scroll inside themselves.
      const scroller = el ? nearestScroller(target, el) : null;
      if (!scroller || scroller.scrollTop > 0) {
        startY.current = null;
        return;
      }
      scrollerRef.current = scroller;
      const t = e.touches[0];
      startY.current = t ? t.clientY : null;
      startX.current = t ? t.clientX : null;
      activeRef.current = false;
    }

    function onTouchMove(e: TouchEvent) {
      if (startY.current == null || !el) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - startY.current;
      const dx = startX.current == null ? 0 : t.clientX - startX.current;
      // Abandon if scrolled away from the top or the pull turned upward.
      const scroller = scrollerRef.current ?? el;
      if (scroller.scrollTop > 0 || dy <= 0) {
        if (activeRef.current) {
          activeRef.current = false;
          setPull(0);
        }
        return;
      }
      // Only a clearly-VERTICAL downward drag is a pull. A horizontal tab-carousel
      // swipe with a little downward drift must not arm the reload — require dy to
      // dominate dx and clear a small floor so an imprecise swipe / finger jitter
      // can't trigger it. (The component's doc always claimed this; the dx check
      // was missing, so any slightly-diagonal swipe armed it.)
      if (!activeRef.current && (dy < 12 || dy < Math.abs(dx) * 1.5)) return;
      activeRef.current = true;
      // Rubber-band damping so the indicator eases rather than tracks 1:1.
      const damped = Math.min(MAX_INDICATOR, dy * 0.5);
      setPull(damped);
    }

    function onTouchEnd() {
      if (activeRef.current && pullRef.current >= PULL_THRESHOLD) {
        setRefreshing(true);
        // Let the spinner paint a frame before the synchronous reload.
        requestAnimationFrame(() => window.location.reload());
      } else {
        setPull(0);
      }
      startY.current = null;
      startX.current = null;
      activeRef.current = false;
      scrollerRef.current = null;
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [scrollRef]);

  if (pull <= 0 && !refreshing) return null;

  const ready = pull >= PULL_THRESHOLD || refreshing;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center"
      style={{ transform: `translateY(${refreshing ? MAX_INDICATOR * 0.5 : pull * 0.6}px)` }}
      aria-hidden
    >
      <div className="mt-2 flex size-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm">
        <RefreshCw
          className={`size-4 ${refreshing ? 'animate-spin' : ''}`}
          style={{ transform: refreshing ? undefined : `rotate(${Math.min(180, (pull / PULL_THRESHOLD) * 180)}deg)`, color: ready ? 'var(--color-primary)' : undefined }}
        />
      </div>
    </div>
  );
}
